import { forwardRef, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Navigation, Share2, MapPin } from "lucide-react";
import { PlaceCoreVerifiedBadge } from "@/components/placecore/public/PlaceCoreVerifiedBadge";
import { PreviewHint } from "./PreviewHint";
import { useCoverAsset } from "@/hooks/useCoverAsset";
import { useGalleryAssets } from "@/hooks/useGalleryAssets";
import { HeroCarousel, type HeroImage } from "@/components/place/HeroCarousel";
import { track } from "@/lib/analytics";
import { useTranslate } from "@/i18n";
import type { Tables } from "@/integrations/supabase/types";

type Producer = Tables<"producers">;

interface ProducerHeroProps {
  producer: Producer;
  onShare: () => void;
  googleMapsUrl: string;
  placeName?: string | null;
  placeSlug?: string | null;
  hasProducts?: boolean;
  onScrollToProducts?: () => void;
  onScrollToMap?: () => void;
  isPreviewMode?: boolean;
  /** Controlled active image index for hero carousel (mobile sync). */
  activeIndex?: number;
  /** Called when user swipes hero to a new index. */
  onHeroIndexChange?: (index: number) => void;
}

export const ProducerHero = forwardRef<HTMLElement, ProducerHeroProps>(
  function ProducerHero(
    { producer, onShare, googleMapsUrl, placeName, placeSlug, isPreviewMode, activeIndex, onHeroIndexChange },
    ref
  ) {
    const t = useTranslate();
    const { url640: coverUrl, asset: coverAsset } = useCoverAsset('producer', producer.id);
    const { urls640: galleryUrls640, assets: galleryAssets } = useGalleryAssets('producer', producer.id);
    const hasCover = Boolean(coverUrl);

    const resolvedPlaceSlug = placeSlug ?? (producer as any).place_slug;
    const resolvedPlaceName = placeName ?? producer.city;

    // Build hero images: cover first, then gallery (deduped)
    const heroImages: HeroImage[] = useMemo(() => {
      const imgs: HeroImage[] = [];
      const seen = new Set<string>();
      if (coverAsset && coverUrl) {
        seen.add(coverAsset.id);
        imgs.push({ id: coverAsset.id, src: coverUrl, alt: producer.name });
      }
      for (let i = 0; i < galleryAssets.length; i++) {
        const a = galleryAssets[i];
        const url = galleryUrls640[i];
        if (a && url && !seen.has(a.id)) {
          seen.add(a.id);
          imgs.push({ id: a.id, src: url, alt: a.alt_text ?? producer.name });
        }
      }
      return imgs;
    }, [coverAsset, coverUrl, galleryAssets, galleryUrls640, producer.name]);

    const handleIndexChange = useCallback(
      (index: number) => {
        onHeroIndexChange?.(index);
        track("producer_hero_image_changed", {
          producer_id: producer.id,
          producer_slug: producer.slug ?? null,
          index,
          images_count: heroImages.length,
        });
      },
      [producer.id, producer.slug, heroImages.length, onHeroIndexChange],
    );

    return (
      <header ref={ref} className="relative">
        {hasCover ? (
          <div className="relative aspect-[4/3] sm:aspect-[16/9] md:aspect-[2.4/1] w-full overflow-hidden">
            {heroImages.length > 1 ? (
              <HeroCarousel
                images={heroImages}
                className="absolute inset-0 w-full h-full"
                onIndexChange={handleIndexChange}
                activeIndex={activeIndex}
              />
            ) : (
              <img
                src={coverUrl!}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/10 pointer-events-none" />
          </div>
        ) : isPreviewMode ? (
          <div className="container pt-6 sm:pt-10">
            <PreviewHint>Den här sidan saknar omslagsbild.</PreviewHint>
          </div>
        ) : (
          <div
            className="relative h-48 sm:h-56 md:h-72 overflow-hidden"
            style={{
              background:
                "linear-gradient(160deg, hsl(var(--secondary)) 0%, hsl(var(--muted)) 60%, hsl(var(--accent)) 100%)",
            }}
          />
        )}

        {/* Content overlay */}
        <div className={hasCover ? "absolute bottom-0 left-0 right-0 pb-6 md:pb-10 pt-20 pointer-events-none" : "pb-0"}>
          <div className="container">
            <div className="max-w-2xl">
              {/* Name + Verified badge */}
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h1
                  className={`font-display text-2xl sm:text-3xl lg:text-4xl font-medium leading-tight ${
                    hasCover ? "text-white" : "text-foreground pt-6 md:pt-8"
                  }`}
                >
                  {producer.name}
                </h1>
                {producer.verified && (
                  <PlaceCoreVerifiedBadge hasCover={hasCover} />
                )}
              </div>

              {/* "Verksam på {Plats}" — clickable */}
              {resolvedPlaceName && (
                <p
                  className={`text-sm sm:text-base mb-4 flex items-center gap-1.5 ${
                    hasCover ? "text-white/75" : "text-muted-foreground"
                  }`}
                >
                  <MapPin className="h-3.5 w-3.5 shrink-0" />
                  {resolvedPlaceSlug ? (
                    <Link
                      to={`/place/${resolvedPlaceSlug}`}
                      data-testid="producer-place-link"
                      className={`underline underline-offset-2 transition-colors pointer-events-auto ${
                        hasCover ? "hover:text-white" : "hover:text-foreground"
                      }`}
                    >
                      Verksam på {resolvedPlaceName}
                    </Link>
                  ) : (
                    <span>Verksam i {resolvedPlaceName}</span>
                  )}
                </p>
              )}

              {/* Desktop actions — discreet, matching Place hero */}
              <div className="hidden md:flex gap-2 pointer-events-auto">
                <Button
                  variant="outline"
                  size="sm"
                  className={`rounded-full ${
                    hasCover
                      ? "bg-white/15 text-white border-white/30 hover:bg-white/25 backdrop-blur-sm"
                      : ""
                  }`}
                  asChild
                >
                  <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer">
                    <Navigation className="h-3.5 w-3.5 mr-1.5" />
                    Ta mig hit
                  </a>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className={`rounded-full ${
                    hasCover
                      ? "bg-white/15 text-white border-white/30 hover:bg-white/25 backdrop-blur-sm"
                      : ""
                  }`}
                  onClick={onShare}
                >
                  <Share2 className="h-3.5 w-3.5 mr-1.5" />
                  {t("cta.share")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>
    );
  }
);
